import { setColumnFilter } from '@civilgrid/ui/hooks';
import type { FilterGroupItem } from '@civilgrid/ui/utils';

/** Server filter property for construction-year filters (projects.constructionYear is a generated text column). */
export const YEAR_FILTER_PROPERTY = 'project.constructionYear';

/** Server filter property for the "My Projects" toolbar shortcut. */
export const MY_PROJECTS_FILTER_PROPERTY = 'project.projectManagerId';

/**
 * % Conflict display for an area over the project's right-of-way area. A zero area (or
 * no right-of-way area) reads "0%", matching the export; "<1%" is reserved for a positive
 * area that rounds down to zero.
 */
export function formatPercentConflict(
  area: number | null | undefined,
  rightOfWayArea: number | null | undefined,
): string {
  if (area == null) return '—';
  const roundedArea = Math.round(area);
  if (roundedArea === 0 || !rightOfWayArea) return '0%';
  const pct = Math.round((roundedArea / rightOfWayArea) * 100);
  return pct ? `${pct.toLocaleString()}%` : '<1%';
}

/**
 * The active construction year, or null when the filter set is anything other
 * than exactly one constructionYear-equals line. Feeds the export analytics'
 * year_filter property.
 */
export function getActiveYearFilter(filters: FilterGroupItem): string | null {
  if (filters.items.length !== 1) return null;
  const item = filters.items[0];
  if (item.type !== 'line') return null;
  if (item.filterType?.id !== YEAR_FILTER_PROPERTY) return null;
  if (item.operatorType?.id !== 'eq') return null;
  return typeof item.value === 'string' && item.value ? item.value : null;
}

/**
 * The year selected via a root-level constructionYear-equals line, regardless
 * of other active filters (unlike the strict `getActiveYearFilter`). Backs the
 * Years dropdown's displayed value.
 */
export function getYearFilterValue(filters: FilterGroupItem): string | null {
  for (const item of filters.items) {
    if (item.type !== 'line') continue;
    if (item.filterType?.id !== YEAR_FILTER_PROPERTY) continue;
    if (item.operatorType?.id !== 'eq') continue;
    if (typeof item.value === 'string' && item.value) return item.value;
  }
  return null;
}

/**
 * The "My Projects" filter set: a single projectManagerId-equals line. The
 * line's own filterType carries a one-option select config so the applied pill
 * shows the user's name instead of the raw id (no table column filters on
 * this property, so the pill has nothing else to resolve the label from).
 */
export function createMyProjectsFilter(userId: string, userName: string): FilterGroupItem {
  return {
    type: 'group',
    id: `my-projects-filter-${Date.now()}`,
    operator: 'and',
    items: [
      {
        type: 'line',
        id: `my-projects-line-${Date.now()}`,
        operator: 'and',
        filterType: {
          id: MY_PROJECTS_FILTER_PROPERTY,
          label: 'Project Manager',
          filterValueConfig: { type: 'select', options: [{ value: userId, label: userName }] },
        },
        operatorType: { id: 'eq', label: 'Equals' },
        value: userId,
      },
    ],
  };
}

/**
 * True only when the filter set is the "My Projects" line for the given user,
 * alone or alongside root-level year lines (structural detection; the adjacent
 * Years dropdown owns the year lines, so they never deactivate the shortcut).
 */
export function isMyProjectsFilterActive(filters: FilterGroupItem, userId: string | null): boolean {
  if (!userId) return false;
  let hasMyProjectsLine = false;
  for (const item of filters.items) {
    if (item.type !== 'line') return false;
    if (item.filterType?.id === MY_PROJECTS_FILTER_PROPERTY) {
      if (item.operatorType?.id !== 'eq' || item.value !== userId) return false;
      hasMyProjectsLine = true;
    } else if (item.filterType?.id !== YEAR_FILTER_PROPERTY) {
      return false;
    }
  }
  return hasMyProjectsLine;
}

/**
 * The "My Projects" toggle-on state: the shortcut's line plus any root-level
 * year lines carried over from the current filters; everything else is
 * replaced, matching homeViewV2's projects table.
 */
export function applyMyProjectsFilter(filters: FilterGroupItem, myProjectsFilter: FilterGroupItem): FilterGroupItem {
  const yearLines = filters.items.filter(
    (item) => item.type === 'line' && item.filterType?.id === YEAR_FILTER_PROPERTY,
  );
  return { ...myProjectsFilter, items: [...myProjectsFilter.items, ...yearLines] };
}

/** The "My Projects" toggle-off state: drops the shortcut's line, keeping the year lines. */
export function removeMyProjectsFilter(filters: FilterGroupItem): FilterGroupItem {
  return {
    ...filters,
    items: filters.items.filter(
      (item) => !(item.type === 'line' && item.filterType?.id === MY_PROJECTS_FILTER_PROPERTY),
    ),
  };
}

/**
 * Replace any root-level constructionYear lines (any operator) with a single
 * equals line for the given year; null clears the year filter entirely. Other
 * filters are preserved. Replacement avoids contradictory year lines, since
 * `setColumnFilter` alone appends.
 */
export function setYearFilter(filters: FilterGroupItem, year: string | null): FilterGroupItem {
  const withoutYear = {
    ...filters,
    items: filters.items.filter((item) => !(item.type === 'line' && item.filterType?.id === YEAR_FILTER_PROPERTY)),
  };
  if (!year) return withoutYear;
  return setColumnFilter(withoutYear, YEAR_FILTER_PROPERTY, { id: 'eq', label: 'Equals' }, year);
}
